<template>
  <v-form>
    <v-card elevation="0">
      <v-card-text>
        <v-select
          v-model="form.sourceId"
          :items="sourcesList"
          item-value="id"
          item-text="name"
          label="Источник данных"
          placeholder="Укажите источник данных"
          clearable
          @change="onSourceChange"
        />

        <v-select
          v-if="form.sourceId"
          v-model="form.schemaName"
          :items="availableSchemas"
          item-value="name"
          item-text="name"
          label="Схема"
          placeholder="Укажите схему"
          clearable
          @change="onSchemaChange"
        />

        <v-autocomplete
          v-if="form.schemaName"
          v-model="form.tableName"
          :items="availableTables"
          item-value="name"
          item-text="name"
          label="Таблица или представление"
          placeholder="Укажите необходимую таблицу или представление"
          clearable
          @change="onTableChange"
        />

        <v-btn
          :disabled="!form.tableName"
          color="primary"
          @click="onSettingsSave"
        >
          Сохранить настройки подключения
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card elevation="0">
      <v-card-title>Настройка соответствия полей формы и данных из источника</v-card-title>
      <v-card-text>
        <v-checkbox
          v-model="form.useInternalRegionFilter"
          label="Использовать фильтр по регионам"
        />
        <v-checkbox
          v-model="isOneFieldCoordinates"
          label="Координаты в одном поле"
        />
        <v-row v-if="!isOneFieldCoordinates">
          <v-col md="6">
            <v-autocomplete
              v-model="form.latitudeColumn"
              :items="availableColumns"
              label="Широта"
              placeholder="Выберите соответствие"
            />
          </v-col>

          <v-col md="6">
            <v-autocomplete
              v-model="form.longitudeColumn"
              :items="availableColumns"
              label="Долгота"
              placeholder="Выберите соответствие"
            />
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col md="6">
            <v-autocomplete
              v-model="form.geometryColumn"
              :items="availableColumns"
              label="Поле с координатами"
              placeholder="Выберите соответствие"
            />
          </v-col>
          <v-col md="6">
            <v-select
              v-model="form.geometryType"
              :items="[{ text: 'Geojson', value: 'geojson' }, { text: 'WKT', value: 'wkt' }]"
              label="Тип координат"
              placeholder="Выберите соответствие"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col md="6">
            <v-autocomplete
              v-model="form.createDateColumn"
              :items="availableColumns"
              label="Дата (для привязки к таймлайну)"
              placeholder="Выберите соответствие"
            />
          </v-col>

          <v-col v-if="false" md="6">
            <v-autocomplete
              v-model="form.updateDateColumn"
              :items="availableColumns"
              label="Дата обновления"
              placeholder="Выберите соответствие"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="formData" elevation="0">
      <v-card-title>Поля формы</v-card-title>
      <v-card-text>
        <template v-for="item in formData.fields">
          <v-autocomplete
            :key="item.id"
            v-model="form.mappings[item.id]"
            :items="availableColumns"
            :label="item.name"
            placeholder="Выберите соответствие"
            @change="onMappingSelect(item.id, $event)"
          />
        </template>
      </v-card-text>
    </v-card>
    <v-alert v-else-if="mapType !== 'region'" type="info">
      Для настройки соответствия полей между слоем в ГИС и источником данных,
      необходимо указать или создать форму для слоя.<br>
      Если форма не указана, то поля и данные будут выводиться "как есть".
    </v-alert>

    <v-card elevation="0">
      <v-card-text>
        <v-btn
          color="primary"
          :disabled="!form.tableName"
          @click="onSettingsSave"
        >
          Сохранить настройки
        </v-btn>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters } from 'vuex';

const createDefaultForm = () => ({
  sourceId: null,
  schemaName: null,
  tableName: null,
  latitudeColumn: null,
  longitudeColumn: null,
  createDateColumn: null,
  updateDateColumn: null,
  mappings: {},
  geometryColumn: null,
  geometryType: null,
});

export default {
  name: 'external-connection-form',

  props: {
    data: {
      type: Object,
      default: createDefaultForm,
    },

    mapType: {
      type: String,
      required: true,
    },

    formData: {
      type: Object,
      required: true,
    },

    hasSettings: {
      type: Boolean,
      required: true,
    },
  },

  data: () => ({
    form: createDefaultForm(),
    availableSchemas: [],
    availableTables: [],
    availableColumns: [],
    isOneFieldCoordinates: false,
  }),

  computed: {
    ...mapGetters('externalConnections', {
      sourcesList: 'valuesForList',
    }),
  },

  async created() {
    const data = JSON.parse(JSON.stringify(this.data));

    this.form = {
      ...this.form,
      ...data,
      // eslint-disable-next-line no-nested-ternary
      mappings: data ? (isEmpty(data.mappings) ? {} : data.mappings) : {},
      sourceId: get(data, 'source.id', null),
      tableName: get(data, 'tableName', null),
      schemaName: get(data, 'schemaName', null),
    };

    if (this.form.geometryType) {
      this.isOneFieldCoordinates = true;
    }

    if (this.form.sourceId && !this.hasSettings) {
      await this.onSourceChange(this.form.sourceId);
    }

    this.fetchSchemas(this.form.sourceId);
    this.fetchTables(this.form.schemaName);

    if (data && data.mappings && data.mappings.length) {
      data.mappings.forEach((item) => {
        this.form.mappings[item.fieldId] = item.columnName;
      });
    }

    if (this.form.tableName) {
      this.availableColumns = await this.getAvailableColumns(this.form.tableName);
    }
    this.valuesWithoutChange = Object.assign({}, this.form);
    this.$emit('registerGuard', [this.valuesWithoutChange, this.beforeLeave]);
  },
  methods: {
    ...mapActions('externalConnections', ['getAvailableTables', 'getTableColumns', 'getSchemas']),

    beforeLeave() {
      return this.form;
    },
    onSourceChange(sourceId) {
      this.form.schemaName = null;
      this.form.tableName = null;
      this.resetSettings();
      this.fetchSchemas(sourceId);
    },

    async fetchSchemas(sourceId) {
      if (sourceId) {
        this.availableSchemas = await this.getSchemas(sourceId);
      } else {
        this.availableSchemas = [];
      }
    },

    onSchemaChange(schemaName) {
      this.form.tableName = null;
      this.resetSettings();
      this.fetchTables(schemaName);
    },

    async fetchTables(schemaName) {
      if (schemaName) {
        this.availableTables = await this.getAvailableTables({
          sourceId: this.form.sourceId,
          schemaName,
        });
      } else {
        this.availableTables = [];
      }
    },

    onTableChange(tableName) {
      this.resetSettings();
      this.fetchColumns(tableName);
    },

    async fetchColumns(tableName) {
      if (tableName) {
        this.availableColumns = await this.getAvailableColumns(tableName);
      } else {
        this.availableColumns = [];
      }
    },

    resetSettings() {
      this.form.latitudeColumn = null;
      this.form.longitudeColumn = null;
      this.form.createDateColumn = null;
      this.form.updateDateColumn = null;
      this.form.mappings = {};
      this.form.geometryColumn = null;
      this.form.geometryType = null;
    },

    onSettingsSave() {
      if (this.isOneFieldCoordinates) {
        this.form.latitudeColumn = null;
        this.form.longitudeColumn = null;
      } else {
        this.form.geometryColumn = null;
        this.form.geometryType = null;
      }
      this.valuesWithoutChange = Object.assign({}, this.form);
      this.$emit('registerGuard', [this.valuesWithoutChange, this.beforeLeave]);
      this.$emit('update', {
        ...this.form,
        sourceId: this.form.sourceId,
      });
    },

    onMappingSelect(fieldId, value) {
      this.$set(this.form.mappings, fieldId, value);
    },

    getAvailableColumns(tableName) {
      return this.getTableColumns({
        sourceId: this.form.sourceId,
        schemaName: this.form.schemaName,
        tableName,
      });
    },
  },
};
</script>
